// ...existing code...
import React from "react";
import { motion } from "framer-motion";
import {
  FaStethoscope,
  FaCloud,
  FaHeartbeat,
  FaShieldAlt,
  FaUserMd,
  FaRegClock,
  FaNotesMedical,
  FaNetworkWired,
  FaIntercom,
  FaOptinMonster,
  FaSatellite,
  FaComment,
  FaVirusSlash,
} from "react-icons/fa";
import "./Healthcare.css";
import { Link } from "react-router-dom";
import { FaFireBurner, FaSection } from "react-icons/fa6";

const FEATURES = [
  {
    icon: <FaVirusSlash/>,
    title: "Software-Defined Networking (SDN) & Network Function Virtualization (NFV)",
    text: "Designing, building, and operating networks which separates the network’s control plane from its data plane. ",
  },
  {
    icon: <FaIntercom />,
    title: "Internet of Things (IoT) Communications",
    text: "Enable everyday physical devices — sensors, actuators, appliances — to connect and exchange information over wired or wireless networks using protocols like Wi-Fi, Bluetooth, NB-IoT, LoRa, etc. ",
  },
  {
    icon: <FaFireBurner/>,
    title: "Optical Communications",
    text: "Use light (often via lasers and optical fiber or free-space paths) to transmit information, offering very high bandwidth and low signal attenuation. ",
  },
  {
    icon: <FaSatellite />,
    title: "Satellite and Space Communications",
    text: "Involve using orbiting artificial satellites and ground-stations to relay radio, voice, information or video signals across vast distances, especially where terrestrial networks are impractical. ",
  },
  {
    icon: <FaNetworkWired />,
    title: "Wireless Sensor Networks (WSNs) & Mesh Networks:",
    text: "Distributed networks of small, low-power sensor nodes that monitor physical or environmental conditions (e.g. temperature, sound, vibration), and cooperatively transmit data via wireless links to a central sink for processing.",
  },
  {
    icon: <FaSection/>,
    title: "Network Security & Privacy in Telecommunication",
    text: "Involves protecting communication networks and user data from unauthorised access, tampering, or disruption, ensuring confidentiality, integrity, and availability of data and services. ",
  },
];

const WHY = [
  {
    icon: <FaShieldAlt />,
    title: "Optimizing Workforce for Telecom Projects",
    text: "We are specialized in IT staffing services, providing skilled professionals adept at managing complex telecommunications projects. Their expertise ensures that telecom initiatives are executed efficiently, meeting deadlines and quality standards ",
  },
  {
    icon: <FaUserMd />,
    title: "Enhancing Digital Presence for Telecom Brands",
    text: "Through its digital marketing solutions, we assist telecommunications companies in establishing a robust online presence. This includes strategies to improve visibility, engage customers, and drive growth in a competitive market.",
  },
  {
    icon: <FaCloud />,
    title: " Developing Tailored Telecom Solutions",
    text: "We offer custom IT solutions, including web design and development, tailored to the unique needs of the telecommunications sector. These solutions aim to streamline operations and enhance customer experiences.",
  },
];

const Card = ({ icon, title, text, i }) => (
  <motion.article
    className="hc-card"
    initial={{ opacity: 0, y: 18, scale: 0.995 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: i * 0.08 }}
    aria-labelledby={`hc-card-${i}-title`}
  >
    <div className="hc-icon" aria-hidden>
      {icon}
    </div>
    <div className="hc-card-body">
      <h3 id={`hc-card-${i}-title`}>{title}</h3>
      <p>{text}</p>
    </div>
  </motion.article>
);

const Telecommunication = () => {
  return (
    <main className="staff-container">
      {/* Header without external image - decorative gradient + shapes via CSS */}
      <header className="staff-header">
             <div className="header-content">
               <h1>TELECOMMUNICATION</h1>
               <p className="subtitle">
            Connecting the world with next-generation telecom solutions. We enable seamless communication through network optimization, cloud telephony, and digital connectivity services.
                </p>
     
               <div className="header-ctas">
                  <Link
               to="/contact" // ✅ Navigate to Contact page
               className="btn-primary"
               aria-label="Contact Lyros"
             >
               Talk to our Expert team
             </Link>
               </div>
             </div>
     
             <svg className="header-decor" viewBox="0 0 800 200" preserveAspectRatio="none" aria-hidden="true">
               <defs>
                 <linearGradient id="gB" x1="0" x2="1">
                   <stop offset="0" stopColor="#0ea5e9" stopOpacity="0.14" />
                   <stop offset="1" stopColor="#6366f1" stopOpacity="0.06" />
                 </linearGradient>
               </defs>
               <path d="M0 140 C160 40 360 240 800 80 L800 200 L0 200 Z" fill="url(#gB)"></path>
             </svg>
           </header>
     

      <section className="container hc-intro">
        <motion.p
          className="section-desc"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
        Telecommunication is the transmission of information—such as voice, content, text, images or video—over distances using electronic signals. It uses various media like wires (cables), optical fibers, radio waves, or satellites. Modern telecom systems often convert analog signals into digital form, enabling efficient, reliable transmission with minimal noise or interference. </motion.p>
      </section>

      <section className="container hc-features">
        <h2 className="section-title">Our Approach</h2>
        <div className="hc-cards">
          {FEATURES.map((f, idx) => (
            <Card key={idx} icon={f.icon} title={f.title} text={f.text} i={idx} />
          ))}
        </div>
      </section>

      <section className="container hc-examples">
        <h2 className="section-title"> Our Telecommunication Services</h2>
        <div className="hc-examples-grid">
          <motion.div
            className="example-card"
            initial={{ opacity: 0, x: -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h4>Data Solutions</h4>
            <p>Business broadband solutions enabling the transmission of voice and data packets, including fibre optics, Digital Subscriber Line (DSL), and leased lines. </p>
          </motion.div>

          <motion.div
            className="example-card"
            initial={{ opacity: 0, x: 12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.08 }}
          >
            <h4>Internet-based Services</h4>
            <p>Platforms operating over the internet, facilitating remote work and expansive communication beyond traditional telephony. </p>
          </motion.div>

          <motion.div
            className="example-card"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.16 }}
          >
            <h4>Cloud-based Services</h4>
            <p>Cloud-hosted communication applications that provide scalability, security, and affordability for businesses moving operations to cloud platforms.</p>
          </motion.div>
        </div>
      </section>

      <section className="container hc-why">
        <h2 className="section-title">Why Choose Lyros</h2>
        <div className="hc-why-grid">
          {WHY.map((w, i) => (
            <motion.div
              className="why-card"
              key={i}
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
            >
              <div className="why-icon" aria-hidden>
                {w.icon}
              </div>
              <div className="why-content">
                <h4>{w.title}</h4>
                <p>{w.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Telecommunication;
// ...existing code...